import { usePermission } from "../hooks/usePermission";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/api";
import toast from "react-hot-toast";
import {
  Plus, X, Pencil, Trash2, Search, Filter, CheckSquare,
  User, Clock, Link as LinkIcon, ChevronRight, Building2,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const fmtMin = (m: number) => { const h = Math.floor(m/60); return `${h}:${String(m%60).padStart(2,'0')}`; };


const STATUSES = ["OPEN", "IN_PROGRESS", "ON_HOLD", "COMPLETED"] as const;
const STATUS_COLORS: Record<string, string> = {
  OPEN:        "bg-gray-100 text-gray-700",
  IN_PROGRESS: "bg-yellow-100 text-yellow-700",
  ON_HOLD:     "bg-orange-100 text-orange-700",
  COMPLETED:   "bg-green-100 text-green-700",
};

const blankForm = { title: "", description: "", referenceLink: "", assignedToId: "", status: "OPEN", taskId: "" };

const SubTasks: React.FC = () => {
  const { user, isAdmin } = useAuth();
  const canDeletePerm = usePermission("tasks", "delete");
  const navigate = useNavigate();

  const [subTasks, setSubTasks] = useState<any[]>([]);
  const [tasks,    setTasks]    = useState<any[]>([]);
  const [users,    setUsers]    = useState<any[]>([]);
  const [loading,  setLoading]  = useState(true);

  /* form */
  const [showForm,    setShowForm]    = useState(false);
  const [editTarget,  setEditTarget]  = useState<any>(null);
  const [form,        setForm]        = useState({ ...blankForm });

  /* filters */
  const [search,       setSearch]       = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterTask,   setFilterTask]   = useState("");
  const [filterUser,   setFilterUser]   = useState("");

  /* ── load ─────────────────────────────────────────────────── */
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [stRes, tRes, uRes] = await Promise.all([
        api.get("/tasks/subtasks/all"),
        api.get("/tasks"),
        api.get("/users"),
      ]);
      setSubTasks(stRes.data);
      setTasks(tRes.data);
      setUsers(uRes.data);
    } catch { toast.error("Failed to load subtasks"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  /* ── filtered ─────────────────────────────────────────────── */
  const filtered = useMemo(() => {
    return subTasks.filter(s => {
      if (filterStatus && s.status !== filterStatus) return false;
      if (filterTask   && s.taskId !== filterTask)   return false;
      if (filterUser   && s.assignedTo?.id !== filterUser) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!s.title?.toLowerCase().includes(q) &&
            !s.description?.toLowerCase().includes(q) &&
            !s.task?.title?.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [subTasks, filterStatus, filterTask, filterUser, search]);

  /* ── submit ───────────────────────────────────────────────── */
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim())  { toast.error("Title is required"); return; }
    if (!form.taskId)        { toast.error("Parent task is required"); return; }
    try {
      if (editTarget) {
        await api.put(`/tasks/subtasks/${editTarget.id}`, form);
        toast.success("Sub-task updated!");
      } else {
        await api.post(`/tasks/${form.taskId}/subtasks`, form);
        toast.success("Sub-task created!");
      }
      setShowForm(false); setEditTarget(null); setForm({ ...blankForm }); load();
    } catch { toast.error("Failed to save sub-task"); }
  };

  const openEdit = (s: any) => {
    setEditTarget(s);
    setForm({
      title:        s.title        || "",
      description:  s.description  || "",
      referenceLink:s.referenceLink|| "",
      assignedToId: s.assignedTo?.id || "",
      status:       s.status       || "OPEN",
      taskId:       s.taskId       || "",
    });
    setShowForm(true);
  };

  const deleteSubTask = async (id: string) => {
    if (!window.confirm("Delete this sub-task?")) return;
    try {
      await api.delete(`/tasks/subtasks/${id}`);
      toast.success("Sub-task deleted!"); load();
    } catch { toast.error("Failed to delete"); }
  };

  const updateStatus = async (id: string, taskId: string, status: string) => {
    await api.put(`/tasks/subtasks/${id}`, { status });
    setSubTasks(prev => prev.map(s => s.id === id ? { ...s, status } : s));
  };

  /* ── stats ────────────────────────────────────────────────── */
  const stats = useMemo(() => ({
    total:     subTasks.length,
    open:      subTasks.filter(s => s.status === "OPEN").length,
    inProg:    subTasks.filter(s => s.status === "IN_PROGRESS").length,
    completed: subTasks.filter(s => s.status === "COMPLETED").length,
  }), [subTasks]);

  /* ── form JSX (inline to prevent focus loss) ──────────────── */
  const formJSX = (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
        <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required
          placeholder="Sub-task title"
          className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"/>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Parent Task *</label>
        <select value={form.taskId} onChange={e => setForm(f => ({ ...f, taskId: e.target.value }))} required
          className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">Select parent task...</option>
          {tasks.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3}
          placeholder="Optional description..."
          className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500 resize-none"/>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
          <select value={form.assignedToId} onChange={e => setForm(f => ({ ...f, assignedToId: e.target.value }))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">Unassigned</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500">
            {STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Reference Link</label>
        <input value={form.referenceLink} onChange={e => setForm(f => ({ ...f, referenceLink: e.target.value }))}
          placeholder="https://..."
          className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"/>
      </div>
    </div>
  );

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Sub-Tasks</h2>
          <p className="text-sm text-gray-500 mt-0.5">{filtered.length} of {subTasks.length} sub-tasks</p>
        </div>
        <button onClick={() => { setEditTarget(null); setForm({ ...blankForm }); setShowForm(true); }}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium">
          <Plus size={15}/> New Sub-Task
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: "Total",       value: stats.total,     color: "bg-gray-50 border-gray-200 text-gray-700" },
          { label: "Open",        value: stats.open,      color: "bg-blue-50 border-blue-200 text-blue-700" },
          { label: "In Progress", value: stats.inProg,    color: "bg-yellow-50 border-yellow-200 text-yellow-700" },
          { label: "Completed",   value: stats.completed, color: "bg-green-50 border-green-200 text-green-700" },
        ].map(s => (
          <div key={s.label} className={`rounded-xl border p-4 ${s.color}`}>
            <p className="text-2xl font-bold">{s.value}</p>
            <p className="text-xs font-medium mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search sub-tasks..."
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500"/>
          </div>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none">
            <option value="">All Statuses</option>
            {STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
          <select value={filterTask} onChange={e => setFilterTask(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none">
            <option value="">All Tasks</option>
            {tasks.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
          </select>
          <select value={filterUser} onChange={e => setFilterUser(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none">
            <option value="">All Employees</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        {(search || filterStatus || filterTask || filterUser) && (
          <button onClick={() => { setSearch(""); setFilterStatus(""); setFilterTask(""); setFilterUser(""); }}
            className="mt-2 text-xs text-red-500 hover:underline flex items-center gap-1">
            <X size={11}/> Clear filters
          </button>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"/>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {["Sub-Task","Parent Task","Assigned To","Logged Hours","Status","Actions"].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(s => (
                <tr key={s.id} className="hover:bg-gray-50 group">
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium text-gray-900">{s.title}</p>
                      {s.description && <p className="text-xs text-gray-400 mt-0.5 max-w-xs truncate">{s.description}</p>}
                      {s.referenceLink && (
                        <a href={s.referenceLink} target="_blank" rel="noreferrer"
                          className="text-xs text-blue-500 flex items-center gap-0.5 mt-0.5 hover:underline"
                          onClick={e => e.stopPropagation()}>
                          <LinkIcon size={10}/> Link
                        </a>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {s.task ? (
                      <button onClick={() => navigate(`/tasks`)}
                        className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                        <CheckSquare size={11}/>{s.task.title}
                      </button>
                    ) : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    {s.assignedTo ? (
                      <span className="flex items-center gap-1.5 text-xs text-gray-600">
                        <div className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[10px] font-bold">
                          {s.assignedTo.name[0]}
                        </div>
                        {s.assignedTo.name}
                      </span>
                    ) : <span className="text-gray-400 text-xs">Unassigned</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-1 text-xs text-gray-600">
                      <Clock size={11} className="text-blue-400"/>
                      {fmtMin(s.totalLoggedMinutes || 0)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <select value={s.status}
                      onChange={e => updateStatus(s.id, s.taskId, e.target.value)}
                      onClick={e => e.stopPropagation()}
                      className={`text-xs px-2 py-1 rounded-lg border font-medium outline-none ${STATUS_COLORS[s.status]}`}>
                      {STATUSES.map(st => <option key={st} value={st}>{st.replace(/_/g, " ")}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
                      <button onClick={() => openEdit(s)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
                        <Pencil size={13}/>
                      </button>
                      {canDeletePerm && (
                        <button onClick={() => deleteSubTask(s.id)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                          <Trash2 size={13}/>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div className="text-center py-14 text-gray-400">
              <CheckSquare size={36} className="mx-auto mb-3 opacity-30"/>
              <p>{subTasks.length === 0 ? "No sub-tasks yet" : "No sub-tasks match the filters"}</p>
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900">
                {editTarget ? "Edit Sub-Task" : "New Sub-Task"}
              </h3>
              <button onClick={() => { setShowForm(false); setEditTarget(null); }}>
                <X size={20} className="text-gray-400 hover:text-gray-600"/>
              </button>
            </div>
            <form onSubmit={submit} className="p-5">
              {formJSX}
              <div className="flex gap-3 pt-4 mt-2 border-t border-gray-100">
                <button type="button"
                  onClick={() => { setShowForm(false); setEditTarget(null); }}
                  className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg text-sm hover:bg-gray-50">
                  Cancel
                </button>
                <button type="submit"
                  className="flex-1 bg-blue-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700">
                  {editTarget ? "Save Changes" : "Create Sub-Task"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubTasks;
